import { useEffect, useState } from 'react';
import { Timestamp, collection, doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { dayKeyIST } from './reception';

export type PresenterDayGrant = {
  uid: string;
  email: string;
  name: string;
  projectId: string;
  dayKey: string;
  active: boolean;
  expiresAt: Timestamp;
};

const groupId = (dayKey: string, projectId: string) => `${dayKey}_${projectId}`;
const grantRef = (db: Firestore, uid: string, dayKey: string, projectId: string) =>
  doc(db, 'presenterDayAccess', groupId(dayKey, projectId), 'members', uid);

const isActive = (grant: PresenterDayGrant | null, dayKey: string, projectId: string, now: number) =>
  !!grant?.active && grant.dayKey === dayKey && grant.projectId === projectId && grant.expiresAt?.toMillis() > now;

/** Access expires at midnight India time, including while the page stays open. */
export function useMyPresenterDayAccess(db: Firestore, uid?: string, projectId?: string | null) {
  const [dayKey, setDayKey] = useState(dayKeyIST());
  const [now, setNow] = useState(Date.now());
  const [grant, setGrant] = useState<PresenterDayGrant | null>(null);
  useEffect(() => {
    const timer = window.setInterval(() => { setDayKey(dayKeyIST()); setNow(Date.now()); }, 30000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    setGrant(null);
    if (!uid || !projectId) return undefined;
    return onSnapshot(grantRef(db, uid, dayKey, projectId),
      (snap) => setGrant(snap.exists() ? snap.data() as PresenterDayGrant : null),
      () => setGrant(null));
  }, [db, uid, dayKey, projectId]);
  return isActive(grant, dayKey, projectId || '', now);
}

/** Manager view of today's approved sales-team presenters for one project. */
export function usePresenterDayGrants(db: Firestore, enabled: boolean, projectId?: string | null) {
  const [dayKey, setDayKey] = useState(dayKeyIST());
  const [now, setNow] = useState(Date.now());
  const [grants, setGrants] = useState<PresenterDayGrant[]>([]);
  useEffect(() => {
    const timer = window.setInterval(() => { setDayKey(dayKeyIST()); setNow(Date.now()); }, 30000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    setGrants([]);
    if (!enabled || !projectId) return undefined;
    return onSnapshot(collection(db, 'presenterDayAccess', groupId(dayKey, projectId), 'members'),
      (snap) => setGrants(snap.docs.map((item) => item.data() as PresenterDayGrant)),
      () => setGrants([]));
  }, [db, enabled, dayKey, projectId]);
  return grants.filter((grant) => isActive(grant, dayKey, projectId || '', now));
}

export async function grantPresenterDayAccess(db: Firestore, projectId: string, user: { uid: string; email: string; name: string }, grantedBy: string) {
  const dayKey = dayKeyIST();
  // The date component is the UTC date; 18:30 UTC is midnight in India.
  const expiresAt = Timestamp.fromDate(new Date(`${dayKey}T18:30:00.000Z`));
  await setDoc(grantRef(db, user.uid, dayKey, projectId), {
    ...user, projectId, dayKey, active: true, expiresAt, grantedBy, grantedAt: serverTimestamp(),
  });
}

export async function revokePresenterDayAccess(db: Firestore, projectId: string, uid: string, revokedBy: string) {
  await updateDoc(grantRef(db, uid, dayKeyIST(), projectId), {
    active: false, revokedBy, revokedAt: serverTimestamp(),
  });
}
